import React, { useState, useEffect } from "react";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import { CircleLoaderComponent } from "../../../components/Loader/CircleLoaderComponent";
import toast from "react-hot-toast";
import { useLazyFetchIffcoUnscannedQrQuery } from "../../../features/tenant/Iffco/IffcoApi";

const IffcoUnscannedQrs = () => {
  // Filters
  const [asOnDate, setAsOnDate] = useState(new Date().toISOString().split("T")[0]);
  const [expiryBucket, setExpiryBucket] = useState("");
  const [productCode, setProductCode] = useState("");
  const [batchCode, setBatchCode] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [tableData, setTableData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);

  // RTK Query hook
  const [triggerUnscannedQr, { isLoading }] = useLazyFetchIffcoUnscannedQrQuery();

  const getParams = () => {
    const vendorData = JSON.parse(sessionStorage.getItem("vendorData") || "{}");
    const token = sessionStorage.getItem("access_token");
    return { tenant_id: vendorData.tenant_id, token };
  };

  const loadData = async () => {
    const { tenant_id, token } = getParams();
    if (!tenant_id || !token) return;

    const queryParams = {
      page: currentPage,
      limit,
      as_on_date: asOnDate,
      expiry_bucket: expiryBucket,
      product_code: productCode,
      batch_code: batchCode,
    };

    Object.keys(queryParams).forEach((key) => {
      if (!queryParams[key]) delete queryParams[key];
    });

    try {
      const res = await triggerUnscannedQr({ tenant_id, token, query: queryParams }).unwrap();
      if (res) {
        const data = res.body?.data || res.data || [];
        const total = Number(res.body?.total_count ?? res.body?.total ?? res.total_count ?? res.total ?? data.length);
        setTableData(data);
        setTotalRows(total);
      } else {
        setTableData([]);
        setTotalRows(0);
      }
    } catch (err) {
      console.error("Error loading Unscanned QRs data:", err);
      toast.error(err?.data?.message || "Failed to load report data");
    }
  };

  useEffect(() => {
    loadData();
  }, [currentPage, limit]);

  const handleApplyFilter = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadData();
  };

  const handleResetFilter = () => {
    setAsOnDate(new Date().toISOString().split("T")[0]);
    setExpiryBucket("");
    setProductCode("");
    setBatchCode("");
    setCurrentPage(1);
    setTimeout(loadData, 50);
  };

  const getColumns = () => {
    const offset = (currentPage - 1) * limit;
    return [
      { name: "S.No", cell: (row, idx) => <span className="fw-semibold text-secondary">{offset + idx + 1}</span>, width: "65px" },
      { name: "QR Code", selector: (row) => row.qr_code || row.unique_code || "-", sortable: true, minWidth: "150px", wrap: true },
      { name: "Batch Code", selector: (row) => row.batch_code || "-", sortable: true, minWidth: "130px", wrap: true },
      { name: "Product Code", selector: (row) => row.product_code || "-", sortable: true, minWidth: "130px", wrap: true },
      { name: "Product Name", selector: (row) => row.product_name || "-", sortable: true, minWidth: "160px", wrap: true },
      { name: "Mfg Date", selector: (row) => row.mfg_date ? new Date(row.mfg_date).toLocaleDateString() : "-", sortable: true, minWidth: "120px" },
      { name: "Expiry Date", selector: (row) => row.expiry_date ? new Date(row.expiry_date).toLocaleDateString() : "-", sortable: true, minWidth: "120px" },
      {
        name: "Days To Expiry",
        cell: (row) => {
          const val = row.days_to_expiry;
          if (val === null || val === undefined) return "-";
          const colorClass = val < 0 ? "text-danger fw-bold" : val <= 30 ? "text-warning fw-bold" : "text-dark";
          return <span className={colorClass}>{val} days</span>;
        },
        sortable: true,
        minWidth: "135px",
      },
      {
        name: "Expiry Bucket",
        cell: (row) => (
          <span className={`badge rounded-pill px-3 py-1 ${row.expiry_bucket === "Expired" ? "bg-danger" : row.expiry_bucket === "0-30" ? "bg-warning text-dark" : "bg-info text-white"}`}>
            {row.expiry_bucket}
          </span>
        ),
        sortable: true,
        minWidth: "130px",
      },
      {
        name: "Status",
        cell: (row) => (
          <span className={`badge rounded-pill px-3 py-1 ${row.status === "Expired" ? "bg-danger" : row.status === "Near Expiry" ? "bg-warning text-dark" : "bg-success"}`}>
            {row.status}
          </span>
        ),
        minWidth: "110px",
      },
    ];
  };

  return (
    <div className="container-fluid py-3">
      <LinksComponent mainLink="IFFCO Reports" subLink="Unscanned QRs" />

      {/* Filter Card Section */}
      <div className="card border shadow-sm mb-4 rounded-3">
        <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-funnel text-primary fs-6"></i>
            <h6 className="m-0 fw-bold text-dark">Filter Reports</h6>
          </div>
          <span className="badge bg-light text-secondary border font-normal">Filter Criteria</span>
        </div>
        <div className="card-body bg-light bg-opacity-25 p-3">
          <form onSubmit={handleApplyFilter} className="row g-3 align-items-end">
            <div className="col-md-3 col-sm-6">
              <label className="form-label text-dark fw-medium small mb-1">As-On Date</label>
              <input
                type="date"
                className="form-control form-control-sm border-gray-300 rounded"
                value={asOnDate}
                onChange={(e) => setAsOnDate(e.target.value)}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <label className="form-label text-dark fw-medium small mb-1">Expiry Bucket</label>
              <select
                className="form-select form-select-sm border-gray-300 rounded"
                value={expiryBucket}
                onChange={(e) => setExpiryBucket(e.target.value)}
              >
                <option value="">All Buckets</option>
                <option value="Expired">Expired</option>
                <option value="0-30">0-30 days</option>
                <option value="31-60">31-60 days</option>
                <option value="61-90">61-90 days</option>
                <option value="90+">90+ days</option>
              </select>
            </div>
            <div className="col-md-3 col-sm-6">
              <label className="form-label text-dark fw-medium small mb-1">Product Code</label>
              <input
                type="text"
                className="form-control form-control-sm border-gray-300 rounded"
                placeholder="Enter Product Code"
                value={productCode}
                onChange={(e) => setProductCode(e.target.value)}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <label className="form-label text-dark fw-medium small mb-1">Batch Code</label>
              <input
                type="text"
                className="form-control form-control-sm border-gray-300 rounded"
                placeholder="Enter Batch Code"
                value={batchCode}
                onChange={(e) => setBatchCode(e.target.value)}
              />
            </div>

            <div className="col-12 d-flex gap-2 justify-content-end pt-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm px-4 fw-medium"
                onClick={handleResetFilter}
              >
                Reset
              </button>
              <button type="submit" className="btn btn-primary btn-sm px-4 fw-medium shadow-sm">
                Apply Filter
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Data Table */}
      <div className="card shadow-sm">
        <div className="card-body p-0">
          {isLoading ? (
            <div className="py-5">
              <CircleLoaderComponent />
            </div>
          ) : (
            <DataTableComponent
              columns={getColumns()}
              data={tableData}
              totalRows={totalRows}
              paginationDefaultPage={currentPage}
              paginationPerPage={limit}
              handlePageChange={(page) => setCurrentPage(page)}
              handlePerRowsChange={(newLimit) => setLimit(newLimit)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default IffcoUnscannedQrs;
